import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { industriesData } from "./IndudtriesData";
import HeadingWithDot from "../../components/common/HeadingWithDot";
import BusinessCard from "../../components/common/BusinessCard";
import HeadingBanner from "../common/HeadingBanner";
import {
  FaArrowRight,
  FaIndustry,
  FaChartLine,
  FaLightbulb,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";

/* ─── Brand tokens ───────────────────────────────────────────────────── */
const ORANGE = "#F5A623";
const BLUE   = "#7B96BD";
const BLUE_D = "#5a7aab";
const BLUE_L = "#dce8f5";

/* ─── Reusable section label ─────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-widest"
      style={{ borderColor: ORANGE + "55", color: ORANGE, background: ORANGE + "10" }}>
      <HiSparkles />
      {text}
    </div>
  );
}

/* ─── Animated counter card ──────────────────────────────────────────── */
function StatCard({ title, text, index }) {
  const colors = [ORANGE, BLUE_D, "#22c55e"];
  return (
    <div
      className="relative overflow-hidden rounded-3xl bg-white p-8 text-center group hover:-translate-y-2 transition-all duration-300"
      style={{ boxShadow: `0 4px 24px ${colors[index]}22` }}
    >
      {/* Corner accent */}
      <div
        className="absolute top-0 right-0 w-20 h-20 rounded-bl-[60px] opacity-10 group-hover:opacity-20 transition-opacity"
        style={{ background: colors[index] }}
      />
      <p
        className="text-5xl font-black tracking-tight"
        style={{ color: colors[index] }}
      >
        {title}
      </p>
      <p className="mt-2 text-slate-500 text-sm font-medium">{text}</p>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────── */
const IndustryDetails = () => {
  const { slug } = useParams();
  const industry = industriesData.find((item) => item.slug === slug);
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();

  if (!industry) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-500 text-lg">
        Industry Not Found
      </div>
    );
  }

  return (
    <div className="bg-[#f8fafd] font-sans">
      {/* ── Banner ─────────────────────────────────────────────────── */}
      <HeadingBanner title={industry.title} />

      <div className="lg:px-20 md:px-10 px-5">

        {/* ── HERO ───────────────────────────────────────────────────── */}
        <section className="py-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left: text */}
            <div>
              <SectionLabel text="Industry Solutions" />

              <h1 className="mt-6 text-5xl font-black leading-[1.15] text-slate-800 tracking-tight">
                {industry.heading}
                <span
                  className="block mt-1"
                  style={{
                    background: `linear-gradient(120deg, ${ORANGE}, ${BLUE_D})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {industry.subHeading}
                </span>
              </h1>

              <div className="mt-8 space-y-4">
                {industry.description.map((text, i) => (
                  <p key={i} className="text-slate-500 leading-relaxed text-base">
                    {text}
                  </p>
                ))}
              </div>

              {/* <div className="mt-10 flex items-center gap-4">
                <button
                  className="flex items-center gap-3 px-8 py-3.5 rounded-2xl text-white font-semibold text-sm
                    hover:scale-105 hover:shadow-xl active:scale-100 transition-all duration-200"
                  style={{
                    background: `linear-gradient(135deg, ${ORANGE}, #e08a10)`,
                    boxShadow: `0 6px 20px ${ORANGE}55`,
                  }}
                >
                  Explore Solutions
                  <FaArrowRight className="text-xs" />
                </button>

                <button
                  className="flex items-center gap-2 px-6 py-3.5 rounded-2xl font-semibold text-sm border-2 transition-all duration-200 hover:shadow-md"
                  style={{ borderColor: BLUE, color: BLUE }}
                >
                  Learn More
                </button>
              </div> */}
            </div>

            {/* Right: image */}
            <div className="relative">
              {/* Decorative ring */}
              <div
                className="absolute -inset-4 rounded-[36px] opacity-20 blur-2xl"
                style={{ background: `linear-gradient(135deg, ${ORANGE}, ${BLUE})` }}
              />

              <img
                src={industry.heroImage}
                alt={industry.title}
                className="relative rounded-3xl w-full h-[480px] object-cover shadow-2xl"
              />

              {/* Floating badge — Enterprise */}
              <div
                className="absolute -left-6 top-8 flex items-center gap-3 bg-white px-4 py-3 rounded-2xl shadow-xl"
                style={{ borderLeft: `4px solid ${ORANGE}` }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: ORANGE + "18" }}
                >
                  <FaIndustry style={{ color: ORANGE }} className="text-lg" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-medium leading-none">Enterprise</p>
                  <p className="text-sm font-bold text-slate-700 mt-0.5">Solutions</p>
                </div>
              </div>

              {/* Floating badge — Growth */}
              <div
                className="absolute -right-5 bottom-10 flex items-center gap-3 bg-white px-4 py-3 rounded-2xl shadow-xl"
                style={{ borderLeft: `4px solid #22c55e` }}
              >
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <FaChartLine className="text-green-500 text-lg" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-medium leading-none">Business</p>
                  <p className="text-sm font-bold text-slate-700 mt-0.5">Growth Focused</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ──────────────────────────────────────────────────── */}
        {/* <section className="pb-16">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "100+", text: "Projects Delivered" },
              { title: "50+",  text: "Global Clients" },
              { title: "24/7", text: "Support Available" },
            ].map((item, i) => (
              <StatCard key={i} index={i} {...item} />
            ))}
          </div>
        </section> */}

        {/* ── SOLUTIONS ──────────────────────────────────────────────── */}
        <section className="py-8">
          {/* Header */}
          <div className="text-center mb-14">
            <SectionLabel text="Solutions" />
            <h2 className="mt-5 text-4xl font-black text-slate-800 tracking-tight">
              Our{" "}
              <span style={{ color: BLUE_D }}>{industry.solutionHeading}</span>{" "}
             IT Solutions
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto text-sm leading-relaxed">
             {industry.solutionDescription}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-7">
            {industry.solutions.map((solution, i) => (
              <div
                key={i}
                className="group relative bg-white rounded-3xl p-8 overflow-hidden
                  hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 cursor-pointer flex flex-col items-center justify-center"
                style={{ boxShadow: `0 2px 16px ${BLUE}18` }}
              >
                {/* Hover gradient overlay */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${ORANGE}08, ${BLUE}12)`,
                  }}
                />

                {/* Icon wrapper */}
                <div >
                  <img src={solution.image} alt="" className="size-14 object-contain" />
                </div>

                <h3 className="relative font-bold text-lg text-slate-800 mt-3">{solution.title}</h3>
                <p className="relative mt-3 text-slate-500 leading-7 text-sm text-center">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PROCESS TIMELINE ───────────────────────────────────────── */}
        <section className="py-8">
          <div className="text-center mb-14">
            <SectionLabel text="Process" />
            <h2 className="mt-5 text-4xl font-black text-slate-800 tracking-tight">
              Our <span style={{ color: BLUE_D }}>Process</span>
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto text-sm leading-relaxed">
              {industry.processDescription}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {industry.processSteps.map((step) => (
              <div
                key={step.index}
                onMouseEnter={() => setActiveIndex(step.index)}
                onMouseLeave={() => setActiveIndex(null)}
                className="group flex gap-6 bg-white p-7 rounded-3xl cursor-default
                  hover:shadow-xl transition-all duration-300"
             
              >
                {/* Step number */}
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-black transition-all duration-300"
                  style={{
                    background: activeIndex === step.index
                      ? `linear-gradient(135deg, ${ORANGE}, #e08a10)`
                      : `linear-gradient(135deg, ${BLUE}, ${BLUE_D})`,
                    boxShadow: activeIndex === step.index
                      ? `0 4px 16px ${ORANGE}44`
                      : `0 4px 16px ${BLUE}44`,
                  }}
                >
                  {step.index}
                </div>

                <div>
                  <h3 className="font-bold text-xl text-slate-800">{step.title}</h3>
                  <p className="mt-2 text-slate-500 leading-7 text-sm">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA BANNER ─────────────────────────────────────────────── */}
        <section className="">
          <div
            className="relative overflow-hidden rounded-[36px] p-14 text-center text-white"
            style={{
              background: `linear-gradient(135deg, #0d1f2d 0%, #1a3a54 50%, #0d2640 100%)`,
            }}
          >
            {/* Decorative circles */}
            <div
              className="absolute -top-16 -left-16 w-64 h-64 rounded-full opacity-20 blur-3xl"
              style={{ background: ORANGE }}
            />
            <div
              className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-20 blur-3xl"
              style={{ background: BLUE }}
            />

            {/* Grid dot pattern */}
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
                backgroundSize: "28px 28px",
              }}
            />

            <div className="relative z-10">
              {/* Glowing icon */}
              <div
                className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                style={{
                  background: ORANGE + "25",
                  border: `1px solid ${ORANGE}50`,
                  boxShadow: `0 0 32px ${ORANGE}40`,
                }}
              >
                <FaLightbulb style={{ color: ORANGE }} className="text-3xl" />
              </div>

              <h2 className="text-4xl font-black tracking-tight">
                Ready to Transform Your Business?
              </h2>

              <p className="mt-4 text-slate-300 max-w-2xl mx-auto leading-relaxed text-base">
                Our innovative industry-focused solutions help businesses optimize
                operations and accelerate growth.
              </p>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button
                onClick={()=>{
                  navigate("/contact-us")
                }}
                  className="flex items-center gap-2 px-8 py-3.5 rounded-2xl font-bold text-sm text-slate-900
                    hover:scale-105 active:scale-100 transition-all duration-200"
                  style={{
                    background: `linear-gradient(135deg, ${ORANGE}, #f0b84a)`,
                    boxShadow: `0 6px 24px ${ORANGE}55`,
                  }}
                >
                  Contact Us Today
                  <FaArrowRight className="text-xs" />
                </button>

                {/* <button
                  className="px-8 py-3.5 rounded-2xl font-semibold text-sm text-white border transition-all duration-200 hover:bg-white/10"
                  style={{ borderColor: "rgba(255,255,255,0.25)" }}
                >
                  View Case Studies
                </button> */}
              </div>
            </div>
          </div>
        </section>

        {/* <BusinessCard /> */}
      </div>

      {/* ── Global styles ─────────────────────────────────────────────── */}
      <style>{`
        * { -webkit-font-smoothing: antialiased; }
      `}</style>
    </div>
  );
};

export default IndustryDetails;